<template>
    <div id="app">
        <div v-if="!isReady" id="app-boot">
            <div v-if="startupFailed" role="alert">
                <p>Unable to load. Please try again.</p>
                <button class="btn btn-primary" @click="initializeApp">
                    Retry
                </button>
            </div>
        </div>
        <LoadingOverlay :visible="isReady ? isLoading : !startupFailed" />
        <router-view v-if="isReady" v-slot="{ Component }">
            <transition name="fade">
                <component :is="Component" />
            </transition>
        </router-view>
    </div>
</template>

<script>
import { computed, onMounted, ref, watch } from 'vue';
import LoadingOverlay from './components/Loading/LoadingOverlay.vue';
import { isLoading } from './plugins/loading';
import { useStore } from 'vuex';
import { resolvePreferredLocale } from '@/utils/locale';

export default {
    name: 'App',
    components: {
        LoadingOverlay,
    },
    setup() {
        const store = useStore();
        const isReady = ref(false);
        const startupFailed = ref(false);

        const tenantTitle = computed(() => store.getters['system/title']);

        watch([tenantTitle, isReady], () => {
            if (isReady.value) {
                document.title = `${tenantTitle.value} - MyBattery365`;
            }
        });

        const initializeApp = async () => {
            startupFailed.value = false;
            try {
                const navigationEntry =
                    performance.getEntriesByType('navigation')[0];
                // Keep the loading shell until both translations and branding
                // are ready; a translucent overlay still exposes placeholders.
                await Promise.all([
                    store.dispatch(
                        'i18n/ensureLocaleLoaded',
                        resolvePreferredLocale()
                    ),
                    store.dispatch(
                        navigationEntry?.type === 'reload'
                            ? 'system/fetchTenantInfoClearCache'
                            : 'system/fetchTenantInfo'
                    ),
                ]);
                isReady.value = true;
            } catch (error) {
                console.error('Failed to initialize app:', error);
                startupFailed.value = true;
            }
        };

        onMounted(initializeApp);

        return {
            isLoading,
            isReady,
            startupFailed,
            initializeApp,
        };
    },
};
</script>

<style lang="scss">
#app {
    font-family: Avenir, Helvetica, Arial, sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-align: center;
    color: #2c3e50;
    display: flex;
    flex-direction: column;
}
</style>
